import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useMe } from '../../../lib/me';
import { useTranslation } from '../../../lib/i18n';
import { localeIsMonthFirst } from '../../../lib/date-locale';

// The home's greeting + date.
//
// `compact` is the mobile-home variant (Figma 6377-22668): a small 16/16 greeting beside the big date.
// `part` splits the two apart for the desktop redesign (Figma "Catch Up states" 7419:47452), where the
// greeting sits at the top-LEFT under the agents island and the date rides the top-RIGHT — so
// `DesktopHomeHeader` renders `part="greeting"` and `part="date"` in different corners. The default
// `full` keeps them together for the mobile stack.
export function GreetingDate({
  compact = false,
  part = 'full',
}: { compact?: boolean; part?: 'full' | 'greeting' | 'date' } = {}) {
  const me = useMe();
  const { t } = useTranslation();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => setNow(new Date()), []);

  const displayName = me?.recipientName?.trim() || me?.firstName?.trim() || me?.email?.trim().split('@')[0] || '';
  const name = displayName.split(/\s+/)[0] ?? '';
  const hour = now.getHours();
  const greetingKey = hour < 12 ? 'home.greeting.morning' : hour < 18 ? 'home.greeting.afternoon' : 'home.greeting.evening';
  const greetingRaw = t(greetingKey, { firstName: name });
  // The copy bakes in `, {firstName}`; when we have no name (still loading, or an account with none),
  // drop the dangling separator rather than render "Good morning, ".
  const greeting = name ? greetingRaw : greetingRaw.replace(/[,，、]\s*$/u, '').trimEnd();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  // The date order follows the BROWSER locale — the user's own chosen format wins. We used to override the
  // region with the account's `siteCountry`, but that made a US-region account read month-first for EVERY
  // browser language, ignoring the user's locale entirely (a machine set to Hebrew still showed `08.31`) —
  // the bug this reverts. Native keeps the account-region override only for the Android Auto car, which has
  // no user locale to read; the web has no such surface. See `lib/date-locale.ts`.
  const monthFirst = useMemo(() => localeIsMonthFirst(navigatorLanguage(), now), [now]);
  const [dateTop, dateBottom] = monthFirst ? [month, day] : [day, month];

  // Both text nodes carry `suppressHydrationWarning` on their DIRECT parent — React only consults it
  // there. The date differs across passes by both its value (`now`) and its ORDER (the browser locale;
  // `navigator` is undefined on the server, so month-vs-day order can only be resolved on the client);
  // the greeting can flip morning/afternoon on the hour boundary between the server render and hydration.
  // Desktop's split greeting sits alone on the top-left, so it takes its natural width; the mobile
  // variants wrap tight (`min-content`) to sit beside the big date.
  const greetingStyle = compact ? styles.greetingCompact : part === 'greeting' ? styles.greetingWide : styles.greeting;
  const greetingNode = (
    <div suppressHydrationWarning style={greetingStyle}>
      {greeting}
    </div>
  );
  // Desktop's split date (top-right) takes its own refined type — 50/53 (Figma 13528-98506) — while
  // the mobile variants keep the larger 60 that sits beside their small greeting.
  //
  // Each line is an ISOLATED LTR run. A numeric date reads left-to-right in every language — Hebrew and
  // Arabic write 08.11 exactly as English does — but the trailing "." is bidi-NEUTRAL, so in an RTL
  // document it takes the paragraph's own direction and lands to the LEFT of the digits: the Hebrew home
  // rendered ".11" over "08" instead of "11." over "08". `dir="ltr"` pins the separator after its number,
  // in every RTL locale at once. This is our own generated numeric run, not localized copy — so unlike
  // Golden Rule 13's user/LLM text it does not follow the app language at all.
  //
  // The isolate sits on the INNER span, never on the line box: the box keeps the document's direction, so
  // `textAlign: 'end'` still MIRRORS (the stack flushes right in LTR, left in RTL) — that part was always
  // correct and must not flip. `suppressHydrationWarning` moves with the text onto its new direct parent,
  // which is the only element React consults.
  const dateNode = (
    <div aria-hidden style={part === 'date' ? styles.dateDesktop : styles.date}>
      <div>
        <span suppressHydrationWarning dir="ltr" style={styles.dateLine}>{dateTop}.</span>
      </div>
      <div>
        <span suppressHydrationWarning dir="ltr" style={styles.dateLine}>{dateBottom}</span>
      </div>
    </div>
  );

  if (part === 'greeting') return <div style={styles.type}>{greetingNode}</div>;
  if (part === 'date') return <div style={styles.type}>{dateNode}</div>;

  return (
    <div style={compact ? { ...styles.root, gap: 12 } : styles.root}>
      {greetingNode}
      {dateNode}
    </div>
  );
}

// `undefined` on the server, which is what `Intl` wants for "the runtime default". The two date lines
// carry `suppressHydrationWarning` for the mismatch that follows.
function navigatorLanguage(): string | undefined {
  return typeof navigator === 'undefined' ? undefined : navigator.language;
}

// The shared display-type run — font, weight, ink — every variant sits in.
const type: CSSProperties = {
  fontFamily: 'var(--brand-font-display)',
  fontWeight: 'var(--sym-weight-semibold)',
  textTransform: 'capitalize',
  color: 'rgb(var(--ink-rgb))',
  fontVariationSettings: '"wdth" 100',
  fontFeatureSettings: '"dlig" 1',
};

const styles: Record<string, CSSProperties> = {
  type,
  // One date line's numeric run. `unicode-bidi: isolate` alongside the element's own `dir="ltr"` keeps
  // the run a self-contained LTR island: the digits and their trailing "." order among THEMSELVES, and
  // the island as a whole still sits wherever the surrounding RTL/LTR line puts it.
  dateLine: { unicodeBidi: 'isolate' },
  // The `full` (mobile) variant: greeting + date on one line, pinned to the trailing edge.
  root: { ...type, display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0, marginInlineStart: 'auto' },
  greeting: { width: 'min-content', textAlign: 'start', fontSize: 32, lineHeight: '34px', letterSpacing: '-0.02em' },
  greetingWide: { textAlign: 'start', fontSize: 32, lineHeight: '34px', letterSpacing: '-0.02em' },
  greetingCompact: { width: 'min-content', textAlign: 'start', fontSize: 16, lineHeight: '16px', letterSpacing: '-0.03em' },
  date: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'end',
    fontSize: 60,
    lineHeight: '48px',
    letterSpacing: '-0.07em',
  },
  // Desktop top-right date (Figma 13528-124765): Archivo SemiBold 50/50, -3.5px (= -0.07em at 50px).
  // The two lines set solid — line-height equals the 50px size, exactly as the frame draws them.
  dateDesktop: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'end',
    fontSize: 50,
    lineHeight: '40px',
    letterSpacing: '-0.05em',
  },
};
